import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Heart, BookOpen } from 'lucide-react';
import * as api from '../lib/api';
import { defaultAvatar } from '../lib/avatarDefaults';
import UpcomingEventsCard from './UpcomingEventsCard';

const categoryColors = {
  guidance: '#4AADA8',
  patience: '#A99CC8',
  marriage: '#E8917B',
  gratitude: '#ACBA9D',
};

const RightSidebar = ({ demoProfile, onNavigate }) => {
  const { profile: authProfile } = useAuth();
  const profile = demoProfile || authProfile;
  const [dua, setDua] = useState(null);
  const [readinessPct, setReadinessPct] = useState(0);

  useEffect(() => {
    api.getDuaOfTheDay().then(r => { if (r.data) setDua(r.data); }).catch(() => {});
  }, []);

  // Fetch real marriage readiness from dashboard
  useEffect(() => {
    if (!profile?.id) return;
    api.getDashboard(profile.id).then(r => {
      setReadinessPct(r.data?.stats?.marriage_readiness_pct || 0);
    }).catch(() => {});
  }, [profile?.id]);

  return (
    <aside className="hidden lg:block col-span-3 p-4 lg:p-6" data-testid="right-sidebar">
      <div className="sidebar-scroll sticky top-24 lg:max-h-[calc(100vh-7rem)] overflow-y-auto overflow-x-hidden -mx-1 px-1 -mt-1 pt-1 pb-4 space-y-6" data-testid="right-sidebar-scroll">
        {/* User Profile Card */}
        <div className="glass-card rounded-arch-xl p-6">
          <div className="flex items-center gap-4 mb-4">
            <img
              src={profile?.avatar_url || defaultAvatar(profile?.gender)}
              alt={profile?.display_name}
              className="w-16 h-16 rounded-full object-cover border-2 border-primary"
            />
            <div>
              {/* Clicking own name → dashboard, others → tooltip */}
              <div className="group/name relative">
                <h3
                  className={`font-heading font-medium text-primary ${
                    onNavigate ? 'cursor-pointer hover:underline' : ''
                  }`}
                  onClick={() => onNavigate?.('dashboard')}
                  data-testid="sidebar-display-name"
                >
                  {profile?.display_name}
                </h3>
              </div>
              <p className="text-sm text-text-muted">{profile?.bio || 'On a growth journey'}</p>
            </div>
          </div>

          {/* Readiness Progress — wired to real dashboard data */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-text-muted flex items-center gap-1">
                <Heart className="w-4 h-4 text-primary" />
                Readiness Journey
              </span>
              <span className="text-sm font-medium text-primary">
                {readinessPct}%
              </span>
            </div>
            <div className="h-3 bg-white/50 rounded-full overflow-hidden">
              <div
                className="h-full progress-bar rounded-full transition-all duration-500"
                style={{ width: `${readinessPct}%` }}
              />
            </div>
          </div>
        </div>

        {/* Dua of the Day (moved from left sidebar) */}
        <div className="glass-card rounded-arch-xl p-5 relative overflow-hidden" data-testid="dua-of-the-day">
          <div className="absolute top-0 right-0 w-20 h-20 bg-primary/5 rounded-full -translate-x-3 -translate-y-6" />
          <h3 className="font-heading font-medium text-primary mb-3 flex items-center gap-2 relative z-10">
            <BookOpen className="w-4 h-4 text-primary" />
            Dua of the Day
          </h3>
          {dua ? (
            <div className="space-y-2.5 relative z-10">
              <p className="text-base text-text-main font-medium text-right leading-loose" dir="rtl" lang="ar" data-testid="dua-arabic">
                {dua.arabic_text}
              </p>
              <p className="text-xs text-primary italic" data-testid="dua-transliteration">
                {dua.transliteration}
              </p>
              <p className="text-xs text-text-muted leading-relaxed" data-testid="dua-translation">
                {dua.translation}
              </p>
              <span
                className="inline-block text-[9px] font-medium px-2 py-0.5 rounded-full mt-1"
                style={{
                  backgroundColor: `${categoryColors[dua.category] || '#4AADA8'}15`,
                  color: categoryColors[dua.category] || '#4AADA8',
                }}
                data-testid="dua-category"
              >
                {dua.category}
              </span>
            </div>
          ) : (
            <p className="text-xs text-text-muted italic">Loading today's dua...</p>
          )}
        </div>

        {/* Upcoming events sit right under the dua so they are above the fold on desktop */}
        <UpcomingEventsCard onNavigate={onNavigate} />

        {/* HIDDEN - re-enable after launch when real user data is available */}
        {/* Community Online widget */}
        {/* Growth Challenges widget */}
      </div>
    </aside>
  );
};

export default RightSidebar;
